import { BaseRow, FirestoreSourceRow, RowData, RowImageData } from '@creative-kit/shared';
import { action, makeObservable, observable } from 'mobx';
import { CloudinaryImage } from '@cloudinary/url-gen';
import { Resize } from '@cloudinary/url-gen/actions/resize';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { FieldValue } from 'firebase-modules';
import { db } from './data';
import type { RootStore } from '.';
import { UiModuleRowEditor } from './ui-module-row-editor';
import { Card } from './card';

export class Row extends BaseRow {
  private ref;
  moduleRowEditor: UiModuleRowEditor;

  constructor(
    id: string,
    sourceId: string,
    sessionId: string,
    data: FirestoreSourceRow,
    rootStore: RootStore,
  ) {
    super(id, sourceId, sessionId, data, rootStore);

    makeObservable<this, 'data'>(this, {
      data: observable.struct,
      updateData: action,
    });

    this.ref = db.sessionSourceRows(this.sessionId, this.sourceId).doc(this.id);
    this.moduleRowEditor = new UiModuleRowEditor();
  }

  updateData(newData: FirestoreSourceRow) {
    this.data = newData;
  }

  // Override getValue to extract filename and extension from image.originalFilename for image sources
  getValue(fieldId: string): string | undefined {
    const source = this.rootStore.sourcesStore.sources[this.sourceId];

    // For image sources, extract filename and extension from image.originalFilename
    // Check field name instead of fieldId since PDF sources use ULID-based fieldIds
    if (source?.type === 'image' && this.data.image?.originalFilename) {
      const fieldName = source.data.fieldNames[fieldId];

      if (fieldName === 'Filename') {
        // Extract filename without extension
        const filename = this.data.image.originalFilename;
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex > 0) {
          return filename.substring(0, lastDotIndex);
        }
        return filename;
      }
      if (fieldName === 'Extension') {
        // Extract extension
        const filename = this.data.image.originalFilename;
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
          return filename.substring(lastDotIndex + 1);
        }
        return '';
      }
    }

    // Fall back to fieldValues for all other cases
    return super.getValue(fieldId);
  }

  // Soft-delete if row is connected to cards
  delete() {
    // Remove connections from all cards before deleting
    if (this.connectedCards.length > 0) {
      this.connectedCards.forEach((card) => {
        const modules = this.rootStore.sessionsStore.sessions[this.sessionId].getModulesForSource(
          this.sourceId,
        );
        modules.forEach((module) => {
          (card as Card).removeConnection(module.id, this.id);
        });
      });
    }

    // Delete the row
    this.ref.delete();
  }

  editRowData(newData: RowData) {
    this.ref.set(
      {
        fieldValues: {
          ...this.data.fieldValues,
          ...newData,
        },
      },
      { merge: true },
    );
  }

  setImage(image: FirestoreSourceRow['image'] | null, folderName?: string) {
    if (image === null || image === undefined) {
      // Delete the image field and clear filename/extension/folderName in fieldValues
      this.ref.update({
        image: FieldValue.delete() as unknown as FirestoreSourceRow['image'],
        'fieldValues.filename': FieldValue.delete() as unknown as string,
        'fieldValues.extension': FieldValue.delete() as unknown as string,
        'fieldValues.folderName': FieldValue.delete() as unknown as string,
      });
    } else {
      // Extract filename and extension from originalFilename
      const { originalFilename } = image;
      const lastDotIndex = originalFilename.lastIndexOf('.');
      const filename =
        lastDotIndex > 0 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
      const extension =
        lastDotIndex > 0 && lastDotIndex < originalFilename.length - 1
          ? originalFilename.substring(lastDotIndex + 1)
          : '';

      // Set the image and populate fieldValues for consistency
      this.ref.set(
        {
          image,
          fieldValues: {
            ...this.data.fieldValues,
            filename,
            extension,
            ...(folderName ? { folderName } : {}),
          },
        },
        { merge: true },
      );
    }
  }

  setAttachment(attachment: FirestoreSourceRow['attachment'] | null) {
    if (attachment === null || attachment === undefined) {
      // Delete the attachment field
      this.ref.update({
        attachment: FieldValue.delete() as unknown as FirestoreSourceRow['attachment'],
      });
    } else {
      // Set the attachment
      this.ref.set({ attachment }, { merge: true });
    }
  }

  getCellImageUrl(height: number) {
    if (this.data.image) {
      const { storage } = this.data.image;
      if (storage.type === 'cloudinary') {
        const image = new CloudinaryImage(
          storage.publicId,
          { cloudName: storage.cloudName },
          { secure: true },
        )
          .resize(Resize.scale().height(height))
          .delivery(format('auto')) // WebP/AVIF when supported
          .delivery(quality('auto')); // Optimal quality/size balance

        return image.toURL();
      }
    }

    return null;
  }

  getAttachmentImageUrl(height: number) {
    if (this.data.attachment) {
      const { storage } = this.data.attachment;
      if (storage.type === 'cloudinary') {
        const image = new CloudinaryImage(
          storage.publicId,
          { cloudName: storage.cloudName },
          { secure: true },
        )
          .resize(Resize.scale().height(height))
          .delivery(format('auto')) // WebP/AVIF when supported
          .delivery(quality('auto')); // Optimal quality/size balance

        return image.toURL();
      }
    }

    return null;
  }

  setFieldImage(fieldId: string, imageData: RowImageData | null) {
    if (imageData === null || imageData === undefined) {
      this.ref.update({
        [`fieldImages.${fieldId}`]: FieldValue.delete(),
      });
    } else {
      this.ref.set(
        {
          fieldImages: {
            ...this.data.fieldImages,
            [fieldId]: imageData,
          },
        },
        { merge: true },
      );
    }
  }

  getFieldImageUrl(fieldId: string, height: number): string | null {
    const fieldImage = this.data.fieldImages?.[fieldId];
    if (fieldImage) {
      const { storage } = fieldImage;
      if (storage.type === 'cloudinary') {
        const image = new CloudinaryImage(
          storage.publicId,
          { cloudName: storage.cloudName },
          { secure: true },
        )
          .resize(Resize.scale().height(height))
          .delivery(format('auto'))
          .delivery(quality('auto'));

        return image.toURL();
      }
    }

    return null;
  }

  /** Returns a Cloudinary URL for the first available fieldImage, or null. */
  getFirstFieldImageUrl(height: number): string | null {
    const fieldImages = this.data.fieldImages;
    if (!fieldImages) return null;
    const firstKey = Object.keys(fieldImages)[0];
    return firstKey ? this.getFieldImageUrl(firstKey, height) : null;
  }

  /** Unified fallback: primary → attachment → first field image URL */
  getUnifiedImageUrl(height: number): string | null {
    return (
      this.getCellImageUrl(height) ||
      this.getAttachmentImageUrl(height) ||
      this.getFirstFieldImageUrl(height)
    );
  }

  /** Unified fallback: primary → attachment → first field image data */
  get unifiedImageData() {
    return this.image || this.attachment || this.firstFieldImage;
  }

  /**
   * Track a download event for this row.
   * Increments downloadCount and updates lastDownloadedAt/lastDownloadedBy.
   */
  trackDownload() {
    const userId = (this.rootStore as RootStore).authStore.user?.uid;
    this.ref.set(
      {
        downloadCount: FieldValue.increment(1) as unknown as number,
        lastDownloadedAt: FieldValue.serverTimestamp() as unknown as Date,
        ...(userId ? { lastDownloadedBy: userId } : {}),
      },
      { merge: true },
    );
  }

  get downloadCount(): number {
    return this.data.downloadCount ?? 0;
  }
}
